import styles from './styles.module.css';
import React, { ReactNode, useEffect, useState } from 'react';
import { CollapsiblePanel } from 'src/components/collapsiblePanel';
import { SectionContainerGrid, SectionContainer } from 'src/components/sectionContainer';
import { LinkWithDescriptionTemplate } from 'src/templates/linkWithDescription/linkWithDescriptionTemplate';
import {
	getDefaultRichTextOptionsPrependId,
	getRichText,
} from 'src/templates/utils/richTextOptions/defaultOptions';
import { Maybe, ContentfulCollapsiblePanelSection } from 'src/graphql-types';
import {
	PrivateCollapsiblePanelSection,
	SERVER_RESPONSE_NO_ACCESS,
	SERVER_RESPONSE_NO_ACCESS_TO_USER_TYPE,
	TableType,
} from 'src/utils/common';
import { Video } from 'src/components/video';
import { useQuery } from '@apollo/react-hooks';
import ProtectedCollapsiblePanelGraphql from './protected-collapsible-panel.graphql';
import StockPersonsTableGraphql from './stock-persons-table.graphql';
import AavsTableGraphql from './aavs-table.graphql';
import { parse, stringify } from 'flatted';
import { useLoginManager } from 'src/utils/login-manager';
import { LinkHelper } from 'src/utils/link-helper';
import {
	aavData,
	AavTable,
	stockPersonData,
	StockPersonTable,
} from 'src/templates/collapsiblePanel/tables';

interface AavsQueryResult {
	allAAVs: aavData[];
}

const AavsTableContent = () => {
	const loginManager = useLoginManager();

	const { data, refetch } = useQuery<AavsQueryResult>(AavsTableGraphql, {
		notifyOnNetworkStatusChange: true,
	});

	useEffect(() => {
		if (loginManager.isLoggedIn && !data) {
			refetch();
		}
	}, [loginManager.isLoggedIn]);

	if (data?.allAAVs?.length) {
		return <AavTable data={data.allAAVs} />;
	}

	return null;
};

interface StockPersonsQueryResult {
	allStockPeople: stockPersonData[];
}

const StockpersonsTableContent = () => {
	const loginManager = useLoginManager();

	const { data, refetch } = useQuery<StockPersonsQueryResult>(StockPersonsTableGraphql, {
		notifyOnNetworkStatusChange: true,
	});

	useEffect(() => {
		if (loginManager.isLoggedIn && !data) {
			refetch();
		}
	}, [loginManager.isLoggedIn]);

	if (data?.allStockPeople?.length) {
		return <StockPersonTable data={data.allStockPeople} />;
	}

	return null;
};

const getPanelId = (parentId?: string, title?: string | null) => {
	if (!title) {
		return undefined;
	}
	return `${parentId ? parentId + '-' : ''}${LinkHelper.parseInternalLink(title)}`;
};

interface CollapsiblePanelContentProps {
	data?: Maybe<ContentfulCollapsiblePanelSection>;
	parentId?: string;
}

interface MessageProps {
	message: string;
	title?: string | null;
	showOpened?: boolean | null;
}

const SimpleMessageContent = (props: MessageProps) => {
	const { message, title, showOpened } = props;
	return (
		<CollapsiblePanel title={title} initExpanded={showOpened || false}>
			<SectionContainer>
				<p className={styles.message}>{props.message}</p>
			</SectionContainer>
		</CollapsiblePanel>
	);
};

const CollapsiblePanelContent = (props: CollapsiblePanelContentProps) => {
	if (!props.data) {
		console.error("CollapsiblePanelContent didn't receive props.data", props);
		return <div></div>;
	}

	const {
		contentful_id,
		content,
		imageOnTheRightOfText,
		videoNextToText,
		collectionOfLinksWithDescription,
		buttonLinkText,
		buttonLinkUrl,
		table,
	} = props.data as ContentfulCollapsiblePanelSection;

	const getSplitContent = (left: ReactNode, right: ReactNode) => {
		return (
			<SectionContainerGrid columns="2" className={styles.gridWithImageOnSide}>
				{left}
				{right}
			</SectionContainerGrid>
		);
	};

	const getInfoLinkCollection = () => {
		if (collectionOfLinksWithDescription && collectionOfLinksWithDescription?.length > 0) {
			return (
				<SectionContainerGrid columns="2" className={styles.infoLinksGrid}>
					{collectionOfLinksWithDescription
						.filter((link) => link?.contentful_id)
						.map((link: any, i: number) => {
							return <LinkWithDescriptionTemplate key={`${link.contentful_id}_${i}`} data={link} />;
						})}
				</SectionContainerGrid>
			);
		}
		return undefined;
	};

	let contentJsx = content ? (
		<div>{getRichText(content, getDefaultRichTextOptionsPrependId(props.parentId || ''))}</div>
	) : undefined;

	if (imageOnTheRightOfText?.fluid?.src) {
		const imgJsx = (
			<img className={styles.collapsibleContentImage} src={imageOnTheRightOfText?.fluid?.src} />
		);
		contentJsx = getSplitContent(contentJsx, imgJsx);
	} else if (videoNextToText?.videoFile?.file?.url || videoNextToText?.externalVideoUrl) {
		const videoJsx = (
			<Video
				videoUrl={videoNextToText.videoFile?.file?.url || videoNextToText.externalVideoUrl || '/'}
				thumbnailImageSrc={videoNextToText.thumbnailLink?.thumbnailImage?.fluid?.src}
			/>
		);
		contentJsx = getSplitContent(contentJsx, videoJsx);
	}

	const getTable = (table?: string | null) => {
		switch (table) {
			case TableType.Aavs:
				return <AavsTableContent />;
			case TableType.Stockpersons:
				return <StockpersonsTableContent />;
			default:
				return undefined;
		}
	};

	const buttonJsx =
		buttonLinkText && buttonLinkUrl ? (
			<a className={styles.linkButton} href={buttonLinkUrl} target="_blank">
				{buttonLinkText}
			</a>
		) : undefined;

	return (
		<>
			{contentJsx}
			{getTable(table)}
			{getInfoLinkCollection()}
			{buttonJsx}
		</>
	);
};

interface PrivateCollapsibleProps {
	contentful_id?: string | null;
	showOpened?: boolean | null;
	parentId?: string;
}

interface QueryResult {
	contentfulCollapsiblePanel: ContentfulCollapsiblePanelSection;
}

const PrivateCollapsible = (props: PrivateCollapsibleProps) => {
	const loginManager = useLoginManager();
	const [showContent, setShowContent] = useState(false);
	const { contentful_id, showOpened, parentId } = props;

	const { data, loading, error, refetch, networkStatus } = useQuery<QueryResult>(
		ProtectedCollapsiblePanelGraphql,
		{
			variables: {
				id: contentful_id,
			},
			notifyOnNetworkStatusChange: true,
		}
	);

	useEffect(() => {
		if (loginManager.isLoggedIn && !data) {
			refetch();
		}
		setShowContent(loginManager.isLoggedIn);
	}, [loginManager.isLoggedIn]);

	const notLoggedIn = error && error.message === SERVER_RESPONSE_NO_ACCESS;
	const notAuthorised = error && error.message === SERVER_RESPONSE_NO_ACCESS_TO_USER_TYPE;

	if (loading || notLoggedIn || notAuthorised) {
		return null;
	} else if (error) {
		return (
			<SimpleMessageContent
				message="There was a problem getting the resource, please try again later."
				showOpened={showOpened}
			/>
		);
	}

	let collapsiblePanel = parse(stringify(data?.contentfulCollapsiblePanel));
	if ((data?.contentfulCollapsiblePanel?.content as any)?.json) {
		collapsiblePanel.content = {
			json: parse((data?.contentfulCollapsiblePanel?.content as any)?.json),
		};
	}

	const id = getPanelId(parentId, data?.contentfulCollapsiblePanel?.title);

	// sometimes, when user logs out, content is still shown, so we check if user is logged in here too.
	return showContent ? (
		<CollapsiblePanel
			id={id}
			title={data?.contentfulCollapsiblePanel?.title}
			initExpanded={showOpened || false}
		>
			<CollapsiblePanelContent data={collapsiblePanel} parentId={id} />
		</CollapsiblePanel>
	) : null;
};

interface Props {
	data: Maybe<ContentfulCollapsiblePanelSection | PrivateCollapsiblePanelSection>;
	parentId?: string;
}

export const CollapsiblePanelTemplate = (props: Props) => {
	if (!props.data) {
		console.error("CollapsiblePanelTemplate didn't receive props.data", props);
		return <div></div>;
	}

	const { contentful_id, title } = props.data;
	const { showOpened } = props.data as ContentfulCollapsiblePanelSection;
	const { contentType } = props.data as PrivateCollapsiblePanelSection;

	const isPublic = contentType === undefined;

	const id = getPanelId(props.parentId, title);

	return isPublic ? (
		<CollapsiblePanel id={id} title={title} initExpanded={showOpened || false}>
			<CollapsiblePanelContent
				data={props.data as ContentfulCollapsiblePanelSection}
				parentId={id}
			/>
		</CollapsiblePanel>
	) : (
		<PrivateCollapsible
			parentId={props.parentId}
			contentful_id={contentful_id}
			showOpened={showOpened || false}
		/>
	);
};
